import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, User, LogOut, Settings, Building, Plus, Lock, Calendar } from 'lucide-react';

export default function Navbar() {
    const location = useLocation();
    const { user, logout, isAdmin, isPartner } = useAuth();
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);

    const isActive = (path) => location.pathname === path;

    return (
        <header className="header">
            <div className="container header-content">
                <Link to="/" className="logo">
                    <img
                        src="/atam-logo.svg"
                        alt="ATAM"
                        style={{ height: '32px', width: 'auto' }}
                    />
                </Link>

                <nav className="nav">
                    <Link to="/" className={`nav-link ${isActive('/') ? 'active' : ''}`}>
                        Home
                    </Link>
                    <Link to="/events" className={`nav-link ${isActive('/events') ? 'active' : ''}`}>
                        Events
                    </Link>
                    <Link to="/calendar" className={`nav-link ${isActive('/calendar') ? 'active' : ''}`}>
                        Calendar
                    </Link>
                    <Link to="/about" className={`nav-link ${isActive('/about') ? 'active' : ''}`}>
                        About
                    </Link>
                    <Link to="/contact" className={`nav-link ${isActive('/contact') ? 'active' : ''}`}>
                        Contact
                    </Link>
                    {user && (
                        <Link to="/dashboard" className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`}>
                            My Events
                        </Link>
                    )}
                    {user && isPartner && (
                        <Link to="/partner" className={`nav-link ${location.pathname.startsWith('/partner') ? 'active' : ''}`}>
                            Partner Portal
                        </Link>
                    )}
                </nav>

                <div className="auth-buttons">
                    {user ? (
                        <div style={{ position: 'relative' }}>
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowUserMenu(!showUserMenu)}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                            >
                                <User size={18} />
                                {user.name}
                            </button>

                            {showUserMenu && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    right: 0,
                                    marginTop: '8px',
                                    background: 'var(--bg-card)',
                                    border: '1px solid var(--border)',
                                    borderRadius: '12px',
                                    padding: '8px',
                                    minWidth: '180px',
                                    zIndex: 50,
                                }}>
                                    <Link
                                        to="/dashboard"
                                        className="btn btn-ghost"
                                        style={{ width: '100%', justifyContent: 'flex-start' }}
                                        onClick={() => setShowUserMenu(false)}
                                    >
                                        <User size={16} />
                                        My Events
                                    </Link>
                                    {isAdmin && (
                                        <Link
                                            to="/admin"
                                            className="btn btn-ghost"
                                            style={{ width: '100%', justifyContent: 'flex-start' }}
                                            onClick={() => setShowUserMenu(false)}
                                        >
                                            <Settings size={16} />
                                            Admin Panel
                                        </Link>
                                    )}
                                    {isPartner && (
                                        <>
                                            <Link
                                                to="/partner"
                                                className="btn btn-ghost"
                                                style={{ width: '100%', justifyContent: 'flex-start' }}
                                                onClick={() => setShowUserMenu(false)}
                                            >
                                                <Building size={16} />
                                                Partner Portal
                                            </Link>
                                            <Link
                                                to="/partner/submit"
                                                className="btn btn-ghost"
                                                style={{ width: '100%', justifyContent: 'flex-start' }}
                                                onClick={() => setShowUserMenu(false)}
                                            >
                                                <Plus size={16} />
                                                Submit Event
                                            </Link>
                                        </>
                                    )}
                                    <Link
                                        to="/change-password"
                                        className="btn btn-ghost"
                                        style={{ width: '100%', justifyContent: 'flex-start' }}
                                        onClick={() => setShowUserMenu(false)}
                                    >
                                        <Lock size={16} />
                                        Change Password
                                    </Link>
                                    <button
                                        className="btn btn-ghost"
                                        style={{ width: '100%', justifyContent: 'flex-start', color: 'var(--error)' }}
                                        onClick={() => {
                                            logout();
                                            setShowUserMenu(false);
                                        }}
                                    >
                                        <LogOut size={16} />
                                        Logout
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <Link to="/login" className="btn btn-primary" style={{ padding: '8px 20px', fontSize: '0.875rem' }}>
                            PartnerLogin
                        </Link>
                    )}
                </div>

                <button
                    className="mobile-menu-btn"
                    onClick={() => setShowMobileMenu(!showMobileMenu)}
                    aria-label="Toggle Menu"
                >
                    {showMobileMenu ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {/* Mobile Menu Backdrop & Overlay */}
            {showMobileMenu && (
                <>
                    <div
                        className="mobile-menu-backdrop"
                        onClick={() => setShowMobileMenu(false)}
                    />
                    <div className="mobile-menu">
                        <div className="mobile-menu-header">
                            <Link to="/" className="logo" onClick={() => setShowMobileMenu(false)}>
                                <img
                                    src="/atam-logo.svg"
                                    alt="ATAM"
                                    style={{ height: '28px', width: 'auto' }}
                                />
                            </Link>
                            <button className="mobile-menu-close" onClick={() => setShowMobileMenu(false)}>
                                <X size={24} />
                            </button>
                        </div>
                        <nav className="mobile-nav">
                            <Link
                                to="/"
                                className={`mobile-nav-link ${isActive('/') ? 'active' : ''}`}
                                onClick={() => setShowMobileMenu(false)}
                            >
                                Home
                            </Link>
                            <Link
                                to="/events"
                                className={`mobile-nav-link ${isActive('/events') ? 'active' : ''}`}
                                onClick={() => setShowMobileMenu(false)}
                            >
                                Events
                            </Link>
                            <Link
                                to="/calendar"
                                className={`mobile-nav-link ${isActive('/calendar') ? 'active' : ''}`}
                                onClick={() => setShowMobileMenu(false)}
                            >
                                Calendar
                            </Link>
                            <Link
                                to="/about"
                                className={`mobile-nav-link ${isActive('/about') ? 'active' : ''}`}
                                onClick={() => setShowMobileMenu(false)}
                            >
                                About
                            </Link>
                            <Link
                                to="/contact"
                                className={`mobile-nav-link ${isActive('/contact') ? 'active' : ''}`}
                                onClick={() => setShowMobileMenu(false)}
                            >
                                Contact
                            </Link>
                            {!user && (
                                <Link
                                    to="/login"
                                    className="mobile-nav-link"
                                    onClick={() => setShowMobileMenu(false)}
                                    style={{ color: 'var(--primary)', fontWeight: 700 }}
                                >
                                    PartnerLogin
                                </Link>
                            )}
                            {user && (
                                <>
                                    <Link
                                        to="/dashboard"
                                        className={`mobile-nav-link ${isActive('/dashboard') ? 'active' : ''}`}
                                        onClick={() => setShowMobileMenu(false)}
                                    >
                                        My Events
                                    </Link>
                                    {isAdmin && (
                                        <Link
                                            to="/admin"
                                            className={`mobile-nav-link ${isActive('/admin') ? 'active' : ''}`}
                                            onClick={() => setShowMobileMenu(false)}
                                        >
                                            Admin Panel
                                        </Link>
                                    )}
                                    {isPartner && (
                                        <>
                                            <Link
                                                to="/partner"
                                                className={`mobile-nav-link ${location.pathname.startsWith('/partner') ? 'active' : ''}`}
                                                onClick={() => setShowMobileMenu(false)}
                                            >
                                                Partner Portal
                                            </Link>
                                            <Link
                                                to="/partner/submit"
                                                className={`mobile-nav-link ${location.pathname.startsWith('/partner/submit') ? 'active' : ''}`}
                                                onClick={() => setShowMobileMenu(false)}
                                            >
                                                Submit Event
                                            </Link>
                                        </>
                                    )}
                                    <Link
                                        to="/change-password"
                                        className="mobile-nav-link"
                                        onClick={() => setShowMobileMenu(false)}
                                    >
                                        Change Password
                                    </Link>
                                </>
                            )}
                        </nav>
                        {user && (
                            <div className="mobile-user-section">
                                <div className="mobile-user-info">
                                    <User size={16} />
                                    <span>{user.name}</span>
                                </div>
                                <button
                                    className="mobile-logout-btn"
                                    onClick={() => {
                                        logout();
                                        setShowMobileMenu(false);
                                    }}
                                >
                                    <LogOut size={16} />
                                    Logout
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </header>
    );
}
